import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import Header from '../../components/header';
import TitleHeader from '../../components/TitleHeader/TitleHeader';
import {
  ConfPeriod,
  DeviceGeneralStatistics,
  Histogram,
  Port,
  WaterMeter,
} from '@thingslog/repositories';
import IndexedWaterMeter from './model/IndexedWaterMeter';
import WaterMeterSelector from './components/WaterMeterSelector';
import { Box, Grid, Paper } from '@mui/material';
import QFlowTable from './components/QFlowTable';
import FlowTable from './components/FlowTable';
import TimeTable from './components/TimeTable';
import GeneralStatisticsTable from './components/GeneralStatisticsTable';
import MissingWaterMeterConfigNotice, {
  InvalidWaterMeter,
  StatisticsErrorState,
} from './components/MissingWaterMeterConfigNotice';
import { useTranslation } from 'react-i18next';
import { ReduxState } from '../../reducers';
import { useSelector } from 'react-redux';
import AuthUtil from '../../common/AuthUtil';
import ConnectedDatePicker from '../../components/PeriodPicker/ConnectedDatePicker';
import { PickerPeriod } from '../../model/Pickers/PickerPeriod';
import { EverySelector } from '@thingslog/ui-components';
import {
  deviceConfigQueryClient,
  devicesQueryClient,
  deviceStatisticsQueryClient,
  initialConfigQueryClient,
} from '../../clients/ReactQueryClients/ReactQueryClients';
import { SensorUtils } from '@thingslog/utils';
import { RecordPeriod } from '../../model/RecordPeriod/RecordPeriod';

const Statistics: React.FC = () => {
  const { t } = useTranslation();
  const [every, setEvery] = useState<number | null>(1);
  const [selectedSensorIndex, setSelectedSensorIndex] = useState<number | null>(null);
  const { deviceNumber } = useParams();
  const fromDate = useSelector((state: ReduxState) => state.period.fromDate);
  const toDate = useSelector((state: ReduxState) => state.period.toDate);
  const selectedDevice = useSelector((state: ReduxState) => state.dev.selectedDevice);
  const auth = useSelector((state: ReduxState) => state.auth);
  const { useDeviceData } = useMemo(() => devicesQueryClient, []);
  const { useDevicePortsConfigData } = useMemo(() => initialConfigQueryClient, []);
  const { useDeviceConfig } = useMemo(() => deviceConfigQueryClient, []);
  const {
    useFlowStatisticsData,
    useGeneralStatisticsData,
    useQFlowStatisticsData,
    useTimeHistogramStatisticsData,
  } = useMemo(() => deviceStatisticsQueryClient, []);
  const deviceQuery = useDeviceData(deviceNumber!, {
    enabled: typeof deviceNumber === 'string',
    retry: false,
    refetchOnWindowFocus: false,
  });
  const {
    data: deviceConfig,
    isLoading: deviceConfigLoading,
    isError: deviceConfigError,
  } = useDeviceConfig(deviceNumber!, { enabled: typeof deviceNumber === 'string' });
  const portsConfigQuery = useDevicePortsConfigData(deviceNumber!, {
    enabled: typeof deviceNumber === 'string',
    retry: false,
    refetchOnWindowFocus: false,
  });
  const portsConfig = portsConfigQuery.data || null;
  const statisticsQueryOptions = useMemo(
    () => ({
      enabled:
        typeof deviceNumber === 'string' &&
        selectedSensorIndex !== null &&
        every !== null &&
        !portsConfigQuery.isError &&
        !deviceQuery.isError,
      retry: false,
      refetchOnWindowFocus: false,
    }),
    [deviceNumber, deviceQuery.isError, every, portsConfigQuery.isError, selectedSensorIndex]
  );
  const flowQuery = useFlowStatisticsData(
    deviceNumber!,
    selectedSensorIndex!,
    fromDate,
    toDate,
    every!,
    statisticsQueryOptions
  );
  const timeQuery = useTimeHistogramStatisticsData(
    deviceNumber!,
    selectedSensorIndex!,
    fromDate,
    toDate,
    every!,
    statisticsQueryOptions
  );
  const qflowQuery = useQFlowStatisticsData(
    deviceNumber!,
    selectedSensorIndex!,
    fromDate,
    toDate,
    every!,
    statisticsQueryOptions
  );
  const generalQuery = useGeneralStatisticsData(
    deviceNumber!,
    selectedSensorIndex!,
    fromDate,
    toDate,
    every!,
    statisticsQueryOptions
  );
  const flow: Histogram | null = flowQuery.data || null;
  const time: Histogram | null = timeQuery.data || null;
  const qflow: Histogram | null = qflowQuery.data || null;
  const general: DeviceGeneralStatistics | null = generalQuery.data || null;
  const flowLoading = flowQuery.isLoading;
  const timeLoading = timeQuery.isLoading;
  const qflowLoading = qflowQuery.isLoading;
  const generalLoading = generalQuery.isLoading;
  const flowError = flowQuery.isError ? t('error_cannot_fetch_flow_stats') : null;
  const timeError = timeQuery.isError ? t('error_cannot_fetch_time_stats') : null;
  const qflowError = qflowQuery.isError ? t('error_cannot_fetch_qflow_stats') : null;
  const generalError = generalQuery.isError ? t('error_cannot_fetch_general_stats') : null;

  const waterMeters = useMemo<IndexedWaterMeter[]>(() => {
    if (!portsConfig) {
      return [];
    }

    return Object.entries(portsConfig).reduce<IndexedWaterMeter[]>(
      (accumulator: IndexedWaterMeter[], [key, port]: [string, Port]) => {
        if (port.sensor['@type'] === 'water_meter' && port.enabled) {
          accumulator.push({ index: Number(key), waterMeter: port.sensor as WaterMeter });
        }

        return accumulator;
      },
      []
    );
  }, [portsConfig]);

  const invalidWaterMeters = useMemo<InvalidWaterMeter[]>(() => {
    return waterMeters.reduce<InvalidWaterMeter[]>(
      (accumulator: InvalidWaterMeter[], indexedWaterMeter: IndexedWaterMeter) => {
        const { waterMeter, index } = indexedWaterMeter;
        const missingFields = [
          waterMeter.diameter === undefined || waterMeter.diameter === null ? 'diameter' : null,
          waterMeter.q3 === undefined || waterMeter.q3 === null ? 'q3' : null,
          waterMeter.r === undefined || waterMeter.r === null ? 'r' : null,
        ].filter(Boolean) as string[];

        if (missingFields.length > 0) {
          accumulator.push({
            index,
            name: waterMeter.name,
            diameter: waterMeter.diameter,
            q3: waterMeter.q3,
            r: waterMeter.r,
            missingFields,
          });
        }

        return accumulator;
      },
      []
    );
  }, [waterMeters]);

  const selectedWaterMeter = useMemo<WaterMeter | null>(() => {
    if (selectedSensorIndex === null) {
      return null;
    }

    return (
      waterMeters.find(
        (indexedWaterMeter: IndexedWaterMeter) => indexedWaterMeter.index === selectedSensorIndex
      )?.waterMeter || null
    );
  }, [selectedSensorIndex, waterMeters]);

  const sensorUnit = useMemo<string>(() => {
    if (!selectedWaterMeter) {
      return '';
    }

    return SensorUtils({ sensor: selectedWaterMeter }).getDefaultUnit();
  }, [selectedWaterMeter]);

  const sensorFlowUnit = useMemo<string | null>(() => {
    if (!selectedWaterMeter) {
      return null;
    }

    return SensorUtils({ sensor: selectedWaterMeter }).getFlowUnitDirectly();
  }, [selectedWaterMeter]);

  const canManageConfig = useMemo(
    () => AuthUtil.hasRole(auth, 'ADMIN') || AuthUtil.hasRole(auth, 'SUPER_ADMIN'),
    [auth]
  );

  const statisticsError = useMemo<StatisticsErrorState | null>(() => {
    if (deviceQuery.isLoading || portsConfigQuery.isLoading || deviceConfigLoading) {
      return null;
    }

    if (deviceQuery.isError) {
      return { type: 'message', message: t('error_cannot_fetch_device') };
    }

    if (deviceConfigError) {
      return { type: 'message', message: t('error_cannot_fetch_device_config') };
    }

    if (portsConfigQuery.isError) {
      return { type: 'message', message: t('error_cannot_fetch_ports_config') };
    }

    if (waterMeters.length === 0) {
      return { type: 'message', message: t('statistics_error_no_water_meters_found') };
    }

    if (invalidWaterMeters.length > 0 && deviceNumber) {
      return {
        type: 'missing_config',
        invalidWaterMeters,
        canManageConfig,
        deviceNumber,
      };
    }

    if (selectedWaterMeter && sensorFlowUnit === null) {
      return {
        type: 'message',
        message: t('statistics_error_flow_unit_not_configured'),
        canManageConfig,
        deviceNumber,
        showSensorConfigAction: true,
      };
    }

    return null;
  }, [
    canManageConfig,
    deviceConfigError,
    deviceConfigLoading,
    deviceNumber,
    deviceQuery.isError,
    deviceQuery.isLoading,
    invalidWaterMeters,
    portsConfigQuery.isError,
    portsConfigQuery.isLoading,
    selectedWaterMeter,
    sensorFlowUnit,
    t,
    waterMeters.length,
  ]);

  useEffect(() => {
    if (waterMeters.length === 0) {
      setSelectedSensorIndex(null);
      return;
    }

    setSelectedSensorIndex((currentSelectedSensorIndex: number | null) => {
      if (
        currentSelectedSensorIndex !== null &&
        waterMeters.some(
          (waterMeter: IndexedWaterMeter) => waterMeter.index === currentSelectedSensorIndex
        )
      ) {
        return currentSelectedSensorIndex;
      }

      return waterMeters[0].index;
    });
  }, [waterMeters]);

  return (
    <Header>
      <Grid container direction="column" alignItems="center" justifyContent="center" rowSpacing={2}>
        {selectedDevice && (
          <Grid item>
            <TitleHeader
              title={t('statistics_header')}
              deviceNumber={selectedDevice.number}
              deviceName={selectedDevice.name}
              customerInfo={selectedDevice.customerInfo}
            />
          </Grid>
        )}
        <Grid item sm={12} md={11} lg={9} xl={8}>
          <Paper elevation={2}>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: 'grey.100',
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }}
            >
              <Box
                sx={{
                  pt: 1,
                  pb: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  justifyContent: {
                    xs: 'center',
                    sm: 'center',
                    md: 'flex-start',
                    lg: 'flex-start',
                    xl: 'flex-start',
                  },
                }}
              >
                <Box sx={{ p: 1 }}>
                  <ConnectedDatePicker period={PickerPeriod.FROM} />
                </Box>
                <Box sx={{ p: 1 }}>
                  <ConnectedDatePicker period={PickerPeriod.TO} />
                </Box>
                {every !== null && deviceConfig && (
                  <Box sx={{ p: 1 }}>
                    <EverySelector
                      confEvery={deviceConfig.every}
                      confPeriod={deviceConfig.recordPeriod}
                      every={every}
                      onEveryChange={setEvery}
                      translation={{
                        day: t('day'),
                        days: t('days'),
                        hour: t('hour'),
                        hours: t('hours'),
                        minute: t('minute'),
                        minutes: t('minutes'),
                        second: t('second'),
                        seconds: t('seconds'),
                        label: t('every'),
                      }}
                    />
                  </Box>
                )}
                <Box sx={{ flexGrow: { xs: 0, sm: 0, md: 1, xl: 1 } }} />
                {selectedSensorIndex !== null && (
                  <Box sx={{ p: 1 }}>
                    <WaterMeterSelector
                      waterMeters={waterMeters}
                      selectedIndex={selectedSensorIndex}
                      setSelectedIndex={setSelectedSensorIndex}
                    />
                  </Box>
                )}
              </Box>
            </Paper>
            <Box sx={{ p: 2 }}>
              {statisticsError ? (
                <MissingWaterMeterConfigNotice error={statisticsError} />
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <GeneralStatisticsTable
                      general={general}
                      loading={generalLoading}
                      error={generalError}
                      unit={sensorUnit}
                      flowUnit={sensorFlowUnit || ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <QFlowTable
                      qFlow={qflow}
                      loading={qflowLoading}
                      error={qflowError}
                      unit={sensorUnit}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FlowTable
                      flow={flow}
                      loading={flowLoading}
                      error={flowError}
                      unit={sensorUnit}
                      flowUnit={sensorFlowUnit || ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TimeTable
                      time={time}
                      loading={timeLoading}
                      error={timeError}
                      unit={sensorUnit}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Header>
  );
};

export default Statistics;
